// in BacktestMain.js
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import './AiTrades.css';
import TradesBacktestChart from './backtestStatsCharts';
import './BacktestMain.css'

export default function BacktestMain() {
  const [docs, setDocs] = useState([]);
  const [filter, setFilter] = useState('all');
  const [investment, setInvestment] = useState('');
  const [leverage, setLeverage] = useState('');
  const [estimatedReturn, setEstimatedReturn] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'desc' });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [advancedPredicate, setAdvancedPredicate] = useState(null);
  const [groupByDate, setGroupByDate] = useState(false);
  const [scoreMin, setScoreMin] = useState('');
  const [scoreMax, setScoreMax] = useState('');
  const [sideFilter, setSideFilter] = useState('all'); // all | buy | sell
  const [reachedPct, setReachedPct] = useState('');
  const [tpHaircutPct, setTpHaircutPct] = useState('');
  const [minPredictedPct, setMinPredictedPct] = useState(''); // filter by TP% >= this
  const [minRiskReward, setMinRiskReward] = useState('');   
  const [pulseFilter, setPulseFilter] = useState('all');
  const [maxAllowedLossPct, setMaxAllowedLossPct] = useState(''); 
  const [maxLossPct, setMaxLossPct] = useState(''); 
  // persist show/hide chart
  const [showChart, setShowChart] = useState(() => {
    const saved = localStorage.getItem('showDayHourChart');
    return saved ? saved === '1' : true;
  });
  useEffect(() => {
    localStorage.setItem('showDayHourChart', showChart ? '1' : '0');
  }, [showChart]);
  

  // date range (persisted)
  const [startDate, setStartDate] = useState(() => localStorage.getItem('aiStart') || '');
  const [endDate, setEndDate]     = useState(() => localStorage.getItem('aiEnd')   || '');
  useEffect(() => { localStorage.setItem('aiStart', startDate); }, [startDate]);
  useEffect(() => { localStorage.setItem('aiEnd',   endDate);   }, [endDate]);

  const userId = localStorage.getItem('userId');
  const isLocal = window.location.hostname === 'localhost';
  const baseUrl = isLocal ? 'http://localhost:5000' : 'https://www.trendscout.club';


  // --- pulse helpers/state ---
const PULSE_OPTIONS = [
  'bullish','bearish','neutral','caution','whipsaw','distribution','recovery'
];

const [savingPulse, setSavingPulse] = useState({});
const [pulseErr, setPulseErr] = useState({});

// Read from backtest only
function readRowPulse(doc) {
  return (doc?.scoringInputs?.marketPulse || '').toLowerCase();
}

function getBacktestId(doc) {
  return doc?._id || doc?.meta?.backtestId || null;
}

async function saveMarketPulseForRow(doc, newPulse, baseUrl) {
  const rowId = getBacktestId(doc);
  if (!rowId) {
    console.warn('[savePulse] missing backtest id for row', doc);
    return;
  }

  // prevent sending empty/invalid or duplicate value
  if (!newPulse) return;
  if (newPulse.toLowerCase() === readRowPulse(doc)) return;

  setSavingPulse((s) => ({ ...s, [rowId]: true }));
  setPulseErr((e) => ({ ...e, [rowId]: '' }));

  try {
    await axios.patch(`${baseUrl}/api/backtest/${rowId}/market-pulse`, {
      marketPulse: newPulse,
    });

    // optimistic update
    setDocs((prev) =>
      prev.map((d) =>
        getBacktestId(d) === rowId
          ? {
              ...d,
              scoringInputs: {
                ...(d.scoringInputs || {}),
                marketPulse: newPulse,
              },
            }
          : d
      )
    );
  } catch (err) {
    console.error('Pulse update failed:', err);
    setPulseErr((e) => ({
      ...e,
      [rowId]: err?.response?.data?.error || 'Save failed',
    }));
  } finally {
    setSavingPulse((s) => ({ ...s, [rowId]: false }));
  }
}


  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    Promise.all([
      axios.get(`${baseUrl}/api/backtest/docs`),
      // axios.get(`${baseUrl}/api/ai-completed-stats`) // if you still use it
    ])
      .then(([docsRes /*, statsRes*/]) => {
        if (cancelled) return;
        setDocs(docsRes.data || []);
        console.log(docsRes.data)
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Fetch failed:', err);
        setError(err);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [baseUrl]);


  // -------- helpers --------
  const extractTrendCore = (trendString = '') => {
    const trend = trendString.toLowerCase();
    if (trend.includes('bullish')) return 'Bullish';
    if (trend.includes('bearish')) return 'Bearish';
    if (trend.includes('neutral')) return 'Neutral';
    return 'Other';
  };

  const inDateRange = (ts) => {
    if (!startDate && !endDate) return true;
    const t = new Date(ts).getTime();
    const start = startDate ? new Date(startDate + 'T00:00:00').getTime() : -Infinity;
    const end   = endDate   ? new Date(endDate   + 'T23:59:59.999').getTime() :  Infinity;
    return t >= start && t <= end;
  };

const filteredDocs = useMemo(() => {
  const byOutcome = docs.filter((doc) => {
    if (filter === 'tp')   return doc.outcome?.targetHit === 'tp';
    if (filter === 'sl')   return doc.outcome?.targetHit === 'sl';
    if (filter === 'none') return doc.outcome?.targetHit === 'none';
    return true;
  });

  // ✅ date
  let arr = byOutcome.filter((d) => inDateRange(d.timestamp));

  // ✅ score range
  arr = arr.filter((d) => {
    const scoreNum = Number(d.analysis?.score); // handles "6.2" too
    if (!Number.isFinite(scoreNum)) return false;

    const min = scoreMin !== '' ? Number(scoreMin) : null;
    const max = scoreMax !== '' ? Number(scoreMax) : null;

    if (min != null && Number.isFinite(min) && scoreNum < min) return false;
    if (max != null && Number.isFinite(max) && scoreNum > max) return false;

    return true;
  });

  // ✅ side
  arr = arr.filter((d) => {
    const rec = (d.analysis?.recommendation || '').toUpperCase();
    if (sideFilter === 'buy')
      return rec.includes('BUY') || rec.includes('LONG');
    if (sideFilter === 'sell')
      return rec.includes('SELL') || rec.includes('SHORT');
    return true;
  });

  // ✅ NEW: market pulse filter
  if (pulseFilter !== 'all') {
    arr = arr.filter((d) => readRowPulse(d) === pulseFilter);
  }

  // ✅ NEW: minimum predicted gain % (requires TP)
  if (minPredictedPct !== '' && !isNaN(parseFloat(minPredictedPct))) {
    const thr = parseFloat(minPredictedPct);
    arr = arr.filter((d) => {
      const tpPct = tpPctBacktest(d);        // % from entry to TP, direction-aware
      return tpPct != null && tpPct >= thr;  // keep only those with TP% >= threshold
    });
  }

  // ✅ NEW: minimum risk:reward
  if (minRiskReward !== '' && !isNaN(parseFloat(minRiskReward))) {
    const rrMin = parseFloat(minRiskReward);
    arr = arr.filter((d) => {
      const rr = riskRewardBacktest(d);
      return rr != null && rr >= rrMin;
    });
  }

// ✅ NEW: max loss filter using initialGain (fallback to outcome.profitLoss)
if (maxLossPct !== '' && !isNaN(parseFloat(maxLossPct))) {
  const thr = Math.abs(parseFloat(maxLossPct)); // 10 => allow down to -10
  arr = arr.filter((d) => {
    const pl =
      typeof d?.initialGain === 'number' ? d.initialGain : null

    if (typeof pl !== 'number') return false;     // or true, if you want to keep unknowns
    return pl > -thr;                            // drop if loss is -10% or worse
  });
}



  return arr;
}, [
  docs,
  filter,
  startDate,
  endDate,
  scoreMin,
  scoreMax,
  sideFilter,
  pulseFilter,      
  minPredictedPct,
  minRiskReward,
  maxLossPct
]);





  const finalDocs = useMemo(
  () => (advancedPredicate ? filteredDocs.filter(advancedPredicate) : filteredDocs),
  [filteredDocs, advancedPredicate]
);

  // headline stats derived from filtered set
  const filteredStats = useMemo(() => {
    const total = filteredDocs.length;
    const tp = filteredDocs.filter((d) => d.outcome?.targetHit === 'tp').length;
    const sl = filteredDocs.filter((d) => d.outcome?.targetHit === 'sl').length;
    const neither = total - tp - sl;

    const sumPL = filteredDocs.reduce(
      (s, d) => s + (typeof d?.outcome?.profitLoss === 'number' ? d.outcome.profitLoss : 0),
      0
    );
    const sumTime = filteredDocs.reduce(
      (s, d) => s + (Number(d?.outcome?.daysToOutcome) || 0),
      0
    );

    const avgProfitLoss = total ? sumPL / total : 0;
    const avgDaysToOutcome = total ? sumTime / total : 0;

    const accuracyRatings = filteredDocs.reduce((acc, d) => {
      const r = d?.outcome?.aiAccuracyRating || 'Unknown';
      acc[r] = (acc[r] || 0) + 1;
      return acc;
    }, {});

    const correct = filteredDocs.filter((d) =>
      d.outcome?.aiAccuracyRating?.toLowerCase().includes('high')
    ).length;
    const incorrect = total - correct;

    return { total, tp, sl, neither, avgProfitLoss, avgDaysToOutcome, accuracyRatings, correct, incorrect };
  }, [filteredDocs]);

  // table sort over filtered set
  const sortedDocs = useMemo(() => {
    const { key, direction } = sortConfig;
    if (!key) return filteredDocs;

    const getValue = (doc) => {
      switch (key) {
        case 'timestamp':     return new Date(doc.timestamp);
        case 'token':         return doc.token;
        case 'source':        return doc.analysis?.source;
        case 'marketCap':     return doc.analysis?.marketCap;
        case 'recommendation':return doc.analysis?.recommendation;
        case 'trend':         return extractTrendCore(doc.analysis?.trendStrength);
        case 'priceChange24h':return parseFloat(doc.analysis?.priceChange24h);
        case 'confidence':    return doc.analysis?.confidenceLevel;
        case 'score':         return doc.analysis?.score;
        case 'scoreMeaning':  return doc.analysis?.scoreMeaning;
        case 'targetHit':     return doc.outcome?.targetHit;
        case 'daysToOutcome': return doc.outcome?.daysToOutcome;
        case 'profitLoss':    return doc.outcome?.profitLoss;
        case 'accuracy':      return doc.outcome?.aiAccuracyRating;
        default:              return null;
      }
    };

    const arr = [...filteredDocs];
    arr.sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);

      if (typeof av === 'string' && typeof bv === 'string') {
        return direction === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      if (av == null) return 1;
      if (bv == null) return -1;
      return direction === 'asc' ? (av - bv) : (bv - av);
    });
    return arr;
  }, [filteredDocs, sortConfig]);

  const toggleSort = (key) => {
    setSortConfig((prev) => (prev.key === key
      ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      : { key, direction: 'asc' }
    ));
  };

  const formatNumberShort = (n) => {
    if (n == null || isNaN(n)) return 'N/A';
    if (n >= 1_000_000_000) return (n / 1_000_000_000).toFixed(1) + ' B';
    if (n >= 1_000_000)     return (n / 1_000_000).toFixed(1) + ' M';
    if (n >= 1_000)         return (n / 1_000).toFixed(1) + ' K';
    return n.toString();
  };


  function sideFromRecommendation(rec = '') {
    const r = rec.toUpperCase();
    if (r.includes('BUY') || r.includes('LONG'))  return 'long';
    if (r.includes('SELL') || r.includes('SHORT')) return 'short';
    return null;
  }

  // Safely read backtest fields with fallbacks from any older shapes
function getBacktestFields(doc) {
  // price reached: prefer backtest top-level, fallback to nested outcome if your API still sends it
  const priceReached =
    (typeof doc?.priceReached === 'number' ? doc.priceReached : null) ??
    (typeof doc?.outcome?.priceReached === 'number' ? doc.outcome.priceReached : null);

  // side & levels: prefer backtest initial*, fallback to long/shortPosition if present
  const side = sideFromRecommendation(doc?.initialRecommendation ?? doc?.analysis?.recommendation);
  const initialEntry =
    (typeof doc?.initialEntry === 'number' ? doc.initialEntry : null) ??
    (side === 'long'
      ? (typeof doc?.longPosition?.entry === 'number' ? doc.longPosition.entry : null)
      : (side === 'short'
          ? (typeof doc?.shortPosition?.entry === 'number' ? doc.shortPosition.entry : null)
          : null));
  const initialSL =
    (typeof doc?.initialSL === 'number' ? doc.initialSL : null) ??
    (side === 'long'
      ? (typeof doc?.longPosition?.sl === 'number' ? doc.longPosition.sl : null)
      : (side === 'short'
          ? (typeof doc?.shortPosition?.sl === 'number' ? doc.shortPosition.sl : null)
          : null));

  return { side, priceReached, initialEntry, initialSL };
}

function reachedGainPctBacktest(doc) {
  const side = sideFromRecommendation(doc?.initialRecommendation);
  const pr = typeof doc?.priceReached === 'number' ? doc.priceReached : null; // favorable
  const e  = doc?.initialEntry;

  if (!side || typeof pr !== 'number' || typeof e !== 'number' || e === 0) return null;

  if (side === 'long')  return ((pr - e) / e) * 100;
  if (side === 'short') return ((e - pr) / e) * 100;
  return null;
}



// SL % for the recommended side (negative). Fallback 0 if missing.
function slPctBacktest(doc) {
  const side = sideFromRecommendation(doc?.initialRecommendation);
  const e = doc?.initialEntry;
  const s = doc?.initialSL;

  if (typeof e === 'number' && typeof s === 'number') {
    if (side === 'long')  return ((s - e) / e) * 100; // negative
    if (side === 'short') return ((e - s) / e) * 100; // negative
  }
  // fallback if SL missing
  return 0;
}

// TP percent for the recommended side using initialEntry/initialTP
function tpPctBacktest(doc) {
  const { side, initialEntry: e } = getBacktestFields(doc);
  const t = typeof doc?.initialTP === 'number' ? doc.initialTP : null;
  if (!side || typeof e !== 'number' || typeof t !== 'number') return null;

  if (side === 'long')  return ((t - e) / e) * 100;
  if (side === 'short') return ((e - t) / e) * 100;
  return null;
}

// Risk:Reward from entry/TP/SL for the recommended side
function riskRewardBacktest(doc) {
  // ✅ prefer the value coming from /api/backtest/docs
  if (typeof doc?.riskReward === 'number') return doc.riskReward;

  // fallback: compute from levels if present
  const rec = (doc?.initialRecommendation || doc?.analysis?.recommendation || '').toUpperCase();
  const side = rec.includes('BUY') || rec.includes('LONG') ? 'long'
             : rec.includes('SELL') || rec.includes('SHORT') ? 'short'
             : null;

  const e = doc?.initialEntry, t = doc?.initialTP, s = doc?.initialSL;
  if (typeof e !== 'number' || typeof t !== 'number' || typeof s !== 'number') return null;

  if (side === 'long') {
    const reward = t - e, risk = e - s;
    if (!(reward > 0) || !(risk > 0)) return null;
    return reward / risk;
  }
  if (side === 'short') {
    const reward = e - t, risk = s - e;
    if (!(reward > 0) || !(risk > 0)) return null;
    return reward / risk;
  }
  return null;
}


const handleCalculate = () => {
  if (!investment || !leverage) return;
  const capital = parseFloat(investment);
  const lev = parseFloat(leverage);
  let totalReturn = 0;

  const docsForCalc = filteredDocs;

  const hasReached = reachedPct !== '' && !isNaN(parseFloat(reachedPct));
  const hasHaircut = tpHaircutPct !== '' && !isNaN(parseFloat(tpHaircutPct));

if (hasReached) {
  const thresh = parseFloat(reachedPct);

  docsForCalc.forEach((doc) => {
    const favorable = reachedGainPctBacktest(doc); // uses priceReached
    const ig =
      (typeof doc?.initialGain === 'number' ? doc.initialGain : null) ??
      (typeof doc?.outcome?.profitLoss === 'number' ? doc.outcome.profitLoss : null);

    // Excel: if favorable >= thresh => +thresh, else => initialGain
    const pct = (favorable != null && favorable >= thresh)
      ? thresh
      : (typeof ig === 'number' ? ig : 0);

    totalReturn += capital * lev * (pct / 100);
  });

    } else if (hasHaircut) {
      const cut = parseFloat(tpHaircutPct);

      // tiny helper (define once outside the loop if you prefer)
      const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

      docsForCalc.forEach((doc) => {
        const tpPct = tpPctBacktest(doc);             // original TP distance in %
        const achieved = reachedGainPctBacktest(doc); // best favorable move in %

        // Interpret "haircut" as: new required TP% = tpPct - cut (your existing semantics)
        // Then clamp so it never exceeds the original TP% and never goes below 0.
        // Examples if tpPct=10:
        //   cut = -100 -> 10 - (-100) = 110 => clamp to 10
        //   cut =  -1  -> 10 - ( -1 ) = 11  => clamp to 10
        //   cut =   5  -> 10 - 5       = 5
        //   cut = 200  -> 10 - 200     = -190 => clamp to 0
        const adjTarget = (tpPct != null) ? clamp(tpPct - cut, 0, tpPct) : null;

        let pct;
        if (adjTarget != null) {
          const officiallyTP = String(doc?.outcome?.targetHit || '').toLowerCase() === 'tp';

          if (officiallyTP || (achieved != null && achieved >= adjTarget)) {
            // Never credit more than the original TP% (already enforced by clamp)
            pct = adjTarget;
          } else {
            // Didn’t meet the (possibly reduced) target → count the SL %
            pct = slPctBacktest(doc); // you removed SL haircut, so this is the base SL%
          }
        } else {
          // If we can't compute TP%, fall back to realized P/L (or SL if preferred)
          const pl = doc?.outcome?.profitLoss;
          pct = (typeof pl === 'number') ? pl : slPctBacktest(doc);
        }

        totalReturn += capital * lev * (pct / 100);
      });

  } else {
    // ---- Existing mode (your current logic) ----
    docsForCalc.forEach((doc) => {
      const pl = doc?.outcome?.profitLoss;
      const acc = doc?.aiAccuracyRating || doc?.outcome?.aiAccuracyRating;
      if (typeof pl === 'number') {
        const isHighAccuracy = String(acc || '').toLowerCase() !== 'low';
        const effectivePL = isHighAccuracy ? pl : -Math.abs(pl);
        totalReturn += capital * lev * (effectivePL / 100);
      }
    });
  }

  setEstimatedReturn(totalReturn.toFixed(2));
};



  // render
if (isLoading) {
  return (
    <div className="loading-modal" role="alert" aria-busy="true" aria-live="polite">
      <div className="loading-content">
        <div className="loader" />
        <p>Loading AI data...</p>
      </div>
    </div>
  );
}

if (error) {
  return (
    <div className="error-box">
      <strong>Could not load data.</strong>
      <div style={{ marginTop: 6 }}>{String(error?.message || error)}</div>
    </div>
  );
}


  return (
    <div className="outcome-table-wrapper-dashboard">
      <div className="outcome-table">
        <h2>📊 AI Completed Trade Performance Dashboard</h2>

        <div className="expanded-content">
          <div className="expanded-section">
            <h4>📈 Performance Summary (filtered)</h4>
            <p><strong>Total Trades:</strong> {filteredStats.total}</p>
            <p><strong>TP Hits:</strong> {filteredStats.tp}</p>
            <p><strong>SL Hits:</strong> {filteredStats.sl}</p>
            <p><strong>Neither Hit:</strong> {filteredStats.neither}</p>
            <p><strong>Correct:</strong> {filteredStats.correct}</p>
            <p><strong>Incorrect:</strong> {filteredStats.incorrect}</p>
          </div>

          <div className="expanded-section">
            <h4>💡 Averages (filtered)</h4>
            <p><strong>Profit/Loss:</strong> {filteredStats.avgProfitLoss.toFixed(2)}%</p>
            <p><strong>Time to Outcome:</strong> {filteredStats.avgDaysToOutcome.toFixed(2)} Hrs</p>
            {userId === 'pziPDtOgJiX06oGuJxNwxE' && (
              <div className="aidashbuttoncontainer">
                <button
                  onClick={async () => {
                    try {
                      const res = await axios.get(`${baseUrl}/api/backtest/exportExcel`, {
                        responseType: 'blob',
                        headers: { 'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
                      });
                      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url; a.download = 'aiBacktest.xlsx';
                      document.body.appendChild(a); a.click(); a.remove();
                      window.URL.revokeObjectURL(url);
                    } catch (err) {
                      console.error('Excel download failed:', err);
                      alert('Could not download Excel file');
                    }
                  }}
                >
                  ⬇️ Download Excel
                </button>
              </div>
            )}
            <div className="aidashbuttoncontainer">
              <button onClick={() => setShowChart((s) => !s)}>
                {showChart ? 'Hide Chart' : 'Show Chart'}
              </button>
            </div>
          </div>

          <div className="expanded-section">
            <h4>🎯 Accuracy Ratings (filtered)</h4>
            {Object.entries(filteredStats.accuracyRatings).map(([rating, count]) => (
              <p key={rating}><strong>{rating}:</strong> {count}</p>
            ))}
          </div>

          <div className="expanded-section">
            <h4>📅 Date Range</h4>
            <label>From</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            <label>To</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            <button onClick={() => { setStartDate(''); setEndDate(''); }}>Clear</button>
          </div>

            <div className="expanded-section">
            <h4>📈 Profit Calculator (filtered)</h4>

            <label>Investment ($):</label>
            <input type="number" value={investment} onChange={(e) => setInvestment(e.target.value)} />

            <label>Leverage (x):</label>
            <input type="number" value={leverage} onChange={(e) => setLeverage(e.target.value)} />
            </div>
           </div>


<div className="expanded-content-settings-aistats">

    {/* Market Pulse + Trade Side */}
  <div className="expanded-section-settings-aistats">
    <label>Trade Side:</label>
    <select value={sideFilter} onChange={(e) => setSideFilter(e.target.value)}>
      <option value="all">All</option>
      <option value="buy">Long / Buy</option>
      <option value="sell">Short / Sell</option>
    </select>

    <label style={{marginTop:5}}>Market Pulse:</label>
    <select value={pulseFilter} onChange={(e) => setPulseFilter(e.target.value)}>
      <option value="all">All</option>
      {PULSE_OPTIONS.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  </div>

  {/* Score Range */}
  <div className="expanded-section-settings-aistats">
    <label>Score Range:</label>
    <div style={{ display: 'flex', gap: '8px' }}>
      <input
        type="number"
        placeholder="Min"
        value={scoreMin}
        onChange={(e) => setScoreMin(e.target.value)}
      />
      <input
        type="number"
        placeholder="Max"
        value={scoreMax}
        onChange={(e) => setScoreMax(e.target.value)}
      />
    </div>
  </div>

  {/* Reached % */}
  <div className="expanded-section-settings-aistats">
    <label>Reached % (optional):</label>
    <input
      type="number"
      placeholder="e.g., 5"
      value={reachedPct}
      onChange={(e) => setReachedPct(e.target.value)}
    />
    <small style={{opacity:.8, display:'block', marginTop:4}}>
      When set, TP/SL are ignored. If a trade reached this % toward its side, we count that %; otherwise we count SL%.
    </small>
  </div>

  {/* TP Haircut */}
  <div className="expanded-section-settings-aistats">
    <label style={{ marginTop: 10 }}>TP Haircut % (optional):</label>
    <input
      type="number"
      placeholder="e.g., 3"
      value={tpHaircutPct}
      onChange={(e) => setTpHaircutPct(e.target.value)}
    />
    <small style={{opacity:.8, display:'block', marginTop:4}}>
      Reduces the TP requirement by this %. If the best move reached the reduced target, we book that reduced %; otherwise we book SL%.
    </small>
  </div>

  {/* Min Predicted Gain */}
  <div className="expanded-section-settings-aistats">
    <label style={{ marginTop: 10 }}>Min Predicted Gain % (by TP):</label>
    <input
      type="number"
      placeholder="e.g., 10"
      value={minPredictedPct}
      onChange={(e) => setMinPredictedPct(e.target.value)}
    />
    <small style={{opacity:.8, display:'block', marginTop:4}}>
      Keeps only trades whose TP is at least this % from entry (direction-aware).
    </small>
  </div>

  {/* Max Allowed Loss */}
  <div className="expanded-section-settings-aistats">
    <label style={{ marginTop: 10 }}>Max Loss % (by initialGain):</label>
    <input
      type="number"
      placeholder="e.g., 10"
      value={maxLossPct}
      onChange={(e) => setMaxLossPct(e.target.value)}
    />
    <small style={{opacity:.8, display:'block', marginTop:4}}>
      Keeps only trades whose initialGain (profitLoss) is not worse than -this %.
    </small>
  </div>


  {/* Min R/R */}
  <div className="expanded-section-settings-aistats">
    <label style={{ marginTop: 10 }}>Min Risk / Reward:</label>
    <input
      type="number"
      placeholder="e.g., 2"
      step="0.01"
      value={minRiskReward}
      onChange={(e) => setMinRiskReward(e.target.value)}
    />
    <small style={{opacity:.8, display:'block', marginTop:4}}>
      Keeps only trades where (reward / risk) ≥ this value, based on entry/TP/SL.
    </small>
  </div>

  {/* Action / Result (span full width if desired) */}
  <div className="expanded-section-settings-aistats span-all">
    <button onClick={handleCalculate} style={{ marginTop: '8px' }}>Calculate</button>
    {estimatedReturn !== null && (
      <p style={{ marginTop: '10px' }}>
        <strong>Estimated Return:</strong> ${estimatedReturn}
      </p>
    )}
  </div>
</div>

      

      {showChart && (
        <div style={{ margin: '16px 0' }}>
          <TradesBacktestChart
            docs={finalDocs}
            height={560}
            groupByDate={groupByDate}
            onChangeGroupMode={setGroupByDate}  // <- NEW
          />
        </div>
      )}




        <div className="table-scroll-container">
          <table>
            <thead>
              <tr>
                <th onClick={() => toggleSort('timestamp')}>Date</th>
                <th onClick={() => toggleSort('token')}>Token</th>
                <th>market pulse</th>
                <th onClick={() => toggleSort('marketCap')}>MC</th>
                <th onClick={() => toggleSort('recommendation')}>Rec.</th>
                <th onClick={() => toggleSort('trend')}>Trend</th>
                <th onClick={() => toggleSort('priceChange24h')}>24h</th>
                <th onClick={() => toggleSort('score')}>Score</th>
                <th onClick={() => toggleSort('targetHit')}>Hit</th>
                <th onClick={() => toggleSort('daysToOutcome')}>Time</th>
                <th onClick={() => toggleSort('profitLoss')}>P/L</th>
                <th onClick={() => toggleSort('accuracy')}>Accuracy</th>
              </tr>
            </thead>
            <tbody>
              {sortedDocs.map((doc, idx) => (
                <tr key={idx}>
                  <td>{new Date(doc.timestamp).toLocaleString()}</td>
                  <td>{doc.token}</td>
                  <td>
                    {userId === 'pziPDtOgJiX06oGuJxNwxE' ? (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <select
                          value={readRowPulse(doc)}
                          onChange={(e) => saveMarketPulseForRow(doc, e.target.value, baseUrl)}
                          disabled={!!savingPulse[getBacktestId(doc)]}
                        >
                          <option value="">—</option>
                          {PULSE_OPTIONS.map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>

                        {savingPulse[getBacktestId(doc)] && (
                          <span style={{ fontSize: 12, opacity: 0.8 }}>Saving…</span>
                        )}
                      </div>
                    ) : (
                      // non-admin users just see the pulse label
                      <span>{readRowPulse(doc) || '—'}</span>
                    )}

                    {pulseErr[getBacktestId(doc)] && (
                      <div style={{ color: 'crimson', fontSize: 12 }}>
                        {pulseErr[getBacktestId(doc)]}
                      </div>
                    )}
                  </td>
                  <td>${formatNumberShort(doc.analysis?.marketCap)}</td>
                  <td>{doc.analysis?.recommendation || '-'}</td>
                  <td>{extractTrendCore(doc.analysis?.trendStrength)}</td>
                  <td>
                    {typeof doc.analysis?.priceChange24h === 'number'
                      ? `${doc.analysis.priceChange24h.toFixed(2)}%`
                      : !isNaN(parseFloat(doc.analysis?.priceChange24h))
                        ? `${parseFloat(doc.analysis.priceChange24h).toFixed(2)}%`
                        : '-'}
                  </td>
                  <td>{doc.analysis?.score || '-'}</td>
                  <td>{doc.outcome?.targetHit || '-'}</td>
                  <td>{doc.outcome?.daysToOutcome || '-'} Hrs</td>
                  <td>{typeof doc.outcome?.profitLoss === 'number' ? `${doc.outcome.profitLoss.toFixed(2)}%` : '-'}</td>
                  <td>{doc.outcome?.aiAccuracyRating || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

      </div>
    </div>
  );
}
